import React, { useState, useEffect, useRef } from "react";
import { ProgressBar, ProgressStep } from "@component/progress-bar";
import { ResumeUpload } from "@component/resume-upload";
import { ResumeAndFilesUpload } from "@component/resume-and-files-upload";
import { JobseekerFirstTimeRegister } from "@component/jobseeker-first-time-register";
import { JobseekerConfirmDetails } from "@component/jobseeker-confirm-details";
import { authLogin, registerJobSeeker, authJobSeekerLoginWithGoogle, authOTPJobSeeker, authSendWelcomEmail } from "@service/auth";
import { RegisterJobSeekerInput } from "@type/auth";
import { CVData } from "@type/cv";
import { processUploadedCV, updateCV } from "@service/cv";
import { getCookie, setCookie } from "@util/cookie";
import { TokenResponse } from "@react-oauth/google";
import { CandidateEmailOTPVerify } from "@container/candidate-email-otp-verify";
import { useQuery } from "@util/useQuery";
import { DailyProvider, useCallObject } from "@daily-co/daily-react";
import { catchErrorLog } from "@util/sentry";
import { ErrorScreen } from "@container/error-screen";
import { DailyCall } from "@daily-co/daily-js";
import { SpeechDetectionProvider } from "@context/speech_detection_context";
import { formatCV, inArrayString, isValidEmail } from "@util/string";
import { CustomInterviewSection, VideoInterviewRef } from "@type/videoInterview";
import { CandidateAIInterviewCompleted } from "@container/candidate-ai-interview-completed";
import ThankYou from "@container/register-success-jobseeker";
import BasicLayout from "@container/layout/basic";
import { getJobDetailGuest } from "@service/job";
import { Job } from "@type/job";
import { InterviewAssessment } from "@type/assessments";
import { getAssessment } from "@service/assessment";
import CustomVoiceInterviewV2 from "@container/custom-voice-interview-v2";
import { Button } from "@component/button";
import { RefreshCwIcon } from "lucide-react";

// Fixed assessment used for the AI Interview step in the jobseeker signup flow.
// (Mirrors the ApplyNowV2 implementation pattern, sourced from the env so the
// id can be tuned per environment without redeploying the bundle.)
const JOBSEEKER_INTERVIEW_ASSESSMENT_ID =
  import.meta.env.VITE_JOBSEEKER_SIGNUP_INTERVIEW_ID || "";

const SUPPORTING_FILES_STORAGE_KEY = "supporting_files_uuids";

// Supporting-file uploads (and everything that consumes their uuids)
// only run when the demo-practice flag is on. Outside demo mode, the
// flow must match the legacy ResumeUpload behaviour exactly.
const isDemoPracticeMode = (): boolean =>
  import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true";

// Reads the temp supporting-file uuids the FE collected during the
// upload step. Same lifecycle as `cv_uuid` in sessionStorage — written
// by ResumeAndFilesUpload, consumed here at activation time. Returns
// an empty array outside demo mode so the activation payload stays
// unchanged for non-demo signups.
const readSupportingFileUuids = (): string[] => {
  if (!isDemoPracticeMode()) return [];
  try {
    const raw = sessionStorage.getItem(SUPPORTING_FILES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Tolerate both the legacy `string[]` shape and the current richer
    // `{ filename: string, ... }[]` shape written by ResumeAndFilesUpload.
    return parsed
      .map((v) => {
        if (typeof v === "string") return v;
        if (v && typeof v.filename === "string") return v.filename;
        return null;
      })
      .filter((v): v is string => typeof v === "string");
  } catch {
    return [];
  }
};
import { trackEvent } from "@util/mixpanel";
import { useCompanySubdomain } from "@context/subdomain_context";
import { isSpaSubdomain } from "@util/subdomain";
import BYOCDomainRegisterPopup from "@container/dialog/dialog-wrong-register";

const JobseekerSignup: React.FC = () => {
  const [isLoadingCompanyInfo, companyInfo] = useCompanySubdomain()
  const [currentPage, setCurrentPage] = useState<string>("resume-upload");
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProcessUploadCV, setProcessUploadCV] = useState<boolean>(false);
  // BE error from /cv/process-uploaded-cv (`response.data.message`).
  // Shown as the body text on the re-upload screen so the user sees
  // the actual cause instead of generic copy. `processCVError` is the
  // legacy short-form slot — currently unused since the single
  // `message` string is now the canonical user-facing copy.
  const [processCVError, setProcessCVError] = useState<string | null>(null);
  const [processCVMessage, setProcessCVMessage] = useState<string | null>(null);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState<boolean>(false);
  const [isHasErrorUpload, setHasErrorUpload] = useState<boolean>(false);
  const [isSkipInterview, setSkipInterview] = useState<boolean>(false);
  const [isLoadingSendWelcomeEmail, setLoadingSendWelcomeEmail] = useState<boolean>(false);
  const [userGoogleInfo, setUserGoogleInfo] = useState(null);
  const [cvData, setCVData] = useState<CVData | null>(null);
  const [userRedirectPath, setUserRedirectPath] = useState<string>("")
  const [error, setError] = useState<string | null>(null);
  const [isLoadingAIInterview, setIsLoadingAIInterview] = useState<boolean>(false);
  const [jobDetail, setJobDetail] = useState<Job | undefined>(undefined);
  const [interviewAssessment, setInterviewAssessment] = useState<InterviewAssessment | null>(null);
  const [formattedSections, setFormattedSections] = useState<CustomInterviewSection[]>([]);
  const [isOpenDialogBYOC, setIsOpenDialogBYOC] = useState(false);
  const queryParams = useQuery();
  const videoInterviewRef = useRef<VideoInterviewRef>(null);

  /**
   * Signups that never run the AI Interview step: it is hidden from the
   * progress bar and Confirm details goes straight to the thank-you page.
   *
   * - `ref` — applying to a specific job; that flow ends at registration.
   * - spa — SPA run their own assessment, so our interview has no place in
   *   their candidates' signup.
   *
   * One flag for both, so the progress bar and the transition can never
   * disagree about whether the step exists — a hidden-but-still-entered step
   * strands the candidate on a page the bar says they are not on.
   */
  const skipAiInterview = !!queryParams.get("ref") || isSpaSubdomain();

  // Add these state variables for the email registration
  const [currentUserID, setCurrentUserID] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  useEffect(() => {
    // Check for cv_uuid in cookies
    const cvUuid = sessionStorage.getItem('cv_uuid');
    const authToken = getCookie('authToken');
    const dataForm = getCookie('__apply_job_guest__');

    if (dataForm) {
      setEmail(dataForm.email);
    }

    if (authToken) {
      setCurrentPage("confirm-details");
      setCurrentStep(2);
    } else if (cvUuid) {
      // Skip resume upload if cv_uuid exists
      setCurrentPage("login");
      setCurrentStep(1);
    }
  }, []);

  useEffect(() => {
    if (queryParams.get("referral")) {
      sessionStorage.setItem("__referral_reg_code__", queryParams.get("referral") || "");
    } else {
      sessionStorage.removeItem("__referral_reg_code__");
    }
  }, [
    queryParams.get("referral"),
  ])

  // Fetch the fixed AI Interview assessment once on mount and prepare
  // its sections in the shape `CustomVoiceInterviewV2` expects (mirrors
  // the assessment loading pattern in ApplyNowV2).
  // Pulls the assessment fixture used for the signup AI interview. Called
  // on mount and from the "Try again" button when the initial load failed
  // (e.g., flaky network or a transient 5xx) so the candidate can recover
  // without a full-page refresh.
  const loadInterviewAssessment = () => {
    if (!JOBSEEKER_INTERVIEW_ASSESSMENT_ID) {
      catchErrorLog(
        "VITE_JOBSEEKER_SIGNUP_INTERVIEW_ID is not configured — AI interview step will be unavailable."
      );
      return;
    }
    setIsLoadingAIInterview(true);
    getAssessment(JOBSEEKER_INTERVIEW_ASSESSMENT_ID)
      .then((resp) => {
        if (!resp || resp.type !== "Interview") return;
        const interviewData = resp as InterviewAssessment;
        setInterviewAssessment(interviewData);
        const sections = interviewData.content.sections.map((section) => ({
          title: section.title,
          instruction: section.instruction,
          duration: section.duration,
          questions: section.questions || [],
          transitions: section.transition ? { before: section.transition } : undefined,
        }));
        setFormattedSections(sections);
      })
      .catch((err) => {
        catchErrorLog(`failed to load jobseeker signup AI interview assessment, err: ${err}`);
      })
      .finally(() => {
        setIsLoadingAIInterview(false);
      });
  };

  useEffect(() => {
    loadInterviewAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a job ref is present, still fetch the job detail for the
  // thank-you redirect / progress bar logic — but no longer use the
  // job's interview assessment (we always use the fixed assessment
  // above).
  const fetchJobDetail = async (jobId: string) => {
    getJobDetailGuest(jobId).then((resp) => {
      setJobDetail(resp);
      if (inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), resp.company_id)) {
        setCurrentPage("thank-you");
        setCurrentStep(4);
      }
    });
  }

  useEffect(() => {
    if (queryParams.get("ref") && currentPage === "ai-interview") {
      fetchJobDetail(queryParams.get("ref") || "");
    }
  }, [queryParams.get("ref"), currentPage])

  let progressSteps: ProgressStep[] = [
    {
      label: "Upload resume",
      isActive: currentStep === 0,
      isCompleted: currentStep > 0,
    },
    {
      label: "Verify email",
      isActive: currentStep === 1,
      isCompleted: currentStep > 1,
    },
    {
      label: "Confirm details",
      isActive: currentStep === 2,
      isCompleted: currentStep > 2,
    },
    {
      label: "AI Interview",
      isActive: currentStep === 3,
      isCompleted: inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "") ? true : currentStep > 3,
      isHide: skipAiInterview,
    },
    {
      label: queryParams.get("ref") ? "Continue" : "Complete",
      isActive: currentStep === 4,
      isCompleted: currentStep > 4,
    },
  ];

  if (jobDetail && inArrayString(import.meta.env.VITE_NEW_APPLY_FLOW_COMPANY_IDS.split(","), jobDetail?.company_id || "")) {
    progressSteps = progressSteps.filter((step) => {
      if (step.label === "AI Interview") {
        return {
          ...step,
          isActive: currentStep === 3,
          isCompleted: true,
        }
      }
      return step;
    }) || []
  }

  // Extracts the user-facing error from a processUploadedCV failure.
  // The service throws raw axios errors for non-2xx; the BE contract is
  // `{ code, message, errors, data, meta }` and we treat
  // `response.data.message` as the canonical human-readable copy.
  const extractProcessCVError = (err: unknown): string | null => {
    const anyErr = err as {
      response?: { data?: { message?: string } };
      message?: string;
    } | undefined;
    console.log('anyErr?.response', anyErr?.response?.data?.message)
    return anyErr?.response?.data?.message
      || anyErr?.message
      || null;
  };

  const handleLoginComplete = (user: RegisterJobSeekerInput) => {
    if (!isValidEmail(user.email)) return;

    // Set loading state to true before API call
    setIsLoading(true);

    if (queryParams.get("ref") && getCookie('__apply_job_guest__')) {
      user.job_id = queryParams.get("ref") || "";
    }

    user.referral_code = sessionStorage.getItem("__referral_reg_code__") || null;

    // Attach any temp supporting-file uuids the user uploaded during the
    // earlier resume step. Demo-only — readSupportingFileUuids returns []
    // outside demo mode, and we only set the field when non-empty so the
    // payload stays identical for legacy signups.
    const supportingUuids = readSupportingFileUuids();
    if (supportingUuids.length > 0) {
      user.supporting_file_uuids = supportingUuids;
    }

    // call register API here
    registerJobSeeker(user)
      .then((resp) => {
        if (resp.status === 201) {
          setCurrentUserID(resp?.data.data?.id || "")
          setUserRedirectPath(resp?.data.data?.redirect_path || "");
          return authOTPJobSeeker(user.email).then(() => {
            setCurrentPage("verify-email");
            setCurrentStep(1);
          }).catch((error) => {
            catchErrorLog(`failed to call auth otp job seeker, err: ${error}`)
          })
        }
      })
      .catch(error => {
        if (error.response.status === 403) {
          setIsOpenDialogBYOC(true)
        } else if (error.response.status === 409) {
          setCurrentUserID(error.response?.data.data?.id || "")
          setUserRedirectPath(error.response?.data.data?.redirect_path || "");
          return authOTPJobSeeker(user.email).then(() => {
            setCurrentPage("verify-email");
            setCurrentStep(1);
          }).catch((error) => {
            catchErrorLog(`failed to call auth otp job seeker, err: ${error}`)
          })
        } else {
          setError(error.response.data.message || "");
          catchErrorLog(`failed to call register job seeker, err: ${error}`)
        }
      })
      .finally(() => {
        // Reset loading state when API call completes
        setIsLoading(false);
      });
  };

  const handleSuccessVerifyEmail = async () => {
    // Log the user in FIRST so the authToken cookie is set before we
    // attempt processUploadedCV. That way, if /cv/process-uploaded-cv
    // errors and we drop into the re-upload retry flow, the retry can
    // just call processUploadedCV again — no second authLogin needed.
    const loginResponse = await authLogin({
      email,
      password,
    })
    if (loginResponse.status === 200) {
      setCookie("authToken", loginResponse.data?.token?.access_token ?? "", import.meta.env.VITE_AUTH_TOKEN_EXPIRED_DAY);
    } else {
      setHasErrorUpload(true);
      setProcessUploadCV(false);
      return;
    }

    const dataForm = getCookie('__apply_job_guest__');
    let respProcessUploadCV: unknown;
    try {
      respProcessUploadCV = await processUploadedCV(
        sessionStorage.getItem("cv_uuid") || "",
        currentUserID,
        dataForm?.name || null,
        dataForm?.country_code || null,
        dataForm?.phone || null,
        dataForm?.email || null,
      )
    } catch (err) {
      const message = extractProcessCVError(err);
      setProcessCVError(null);
      setProcessCVMessage(message);
      setHasErrorUpload(true);
      setProcessUploadCV(false);
      return;
    }
    if (!respProcessUploadCV) {
      // Soft failure path — keep any existing processCVMessage rather
      // than blanking it out.
      setHasErrorUpload(true);
      setProcessUploadCV(false);
      return;
    }
    setProcessCVError(null);
    setProcessCVMessage(null);
    // OTP + post-registration attach succeeded — the supporting-file
    // uuids are now bound to the user server-side, drop them from
    // sessionStorage so a refresh doesn't replay them.
    if (isDemoPracticeMode()) {
      sessionStorage.removeItem(SUPPORTING_FILES_STORAGE_KEY)
    }
    setProcessUploadCV(false);
  }

  const handleProfileSave = (profile: CVData): void => {
    setIsLoading(true);

    const workExperience = profile.work_experience.sort((a, b) => {
      if (a.is_current) return -1;
      if (b.is_current) return 1;
      const yearDiff = b.period_end_year - a.period_end_year;
      if (yearDiff !== 0) return yearDiff;
      return b.period_end_month - a.period_end_month;
    });

    let workExperienceYear = 0;
    for (const experience of workExperience) {
      const startYear = experience.period_start_year;
      const endYear = experience.is_current ? new Date().getFullYear() : experience.period_end_year;
      workExperienceYear += Math.max(0, endYear - startYear);
    }

    const skillCount = profile.skills ? profile.skills.length : 0;

    // @ts-ignore
    updateCV({
      'CV_data': {
        ...profile,
        work_experience: workExperience,
      } as CVData
    })
      .then(() => {
        setCVData(profile);

        // skip AI interview (job-ref applications, and the spa subdomain)
        if (skipAiInterview) {
          setCurrentPage("thank-you");
          setCurrentStep(4);
        } else {
          setCurrentPage("ai-interview");
          setCurrentStep(3);
        }

        trackEvent('resume_details_confirmed', {
          skill_count: skillCount,
          work_experience_year: workExperienceYear
        })
        sessionStorage.removeItem("cv_uuid")
        if (isDemoPracticeMode()) {
          sessionStorage.removeItem(SUPPORTING_FILES_STORAGE_KEY)
        }
      })
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error updating profile", error);
      });

  };

  const handleRetryProcessUploadCV = async () => {
    // The authToken cookie is set during the initial Google login /
    // email auth flow (before processUploadedCV runs), so the retry
    // can call processUploadedCV directly — no re-login required.
    setProcessUploadCV(true)
    const dataForm = getCookie('__apply_job_guest__');
    let respProcessUploadCV: unknown;
    try {
      respProcessUploadCV = await processUploadedCV(
        sessionStorage.getItem("cv_uuid") || "",
        currentUserID,
        dataForm?.name || null,
        dataForm?.country_code || null,
        dataForm?.phone || null,
        dataForm?.email || null,
      )
    } catch (err) {
      const message = extractProcessCVError(err);
      setProcessCVError(null);
      setProcessCVMessage(message);
      setHasErrorUpload(true);
      setProcessUploadCV(false);
      return
    }
    if (!respProcessUploadCV) {
      setHasErrorUpload(true);
      setProcessUploadCV(false);
      return
    }
    setProcessCVError(null);
    setProcessCVMessage(null);
    setHasErrorUpload(false);
    setProcessUploadCV(false);
  }

  const handleGoogleSignup = async (tokenResponse: TokenResponse) => {
    setIsLoadingGoogle(true);
    setIsLoading(true);

    try {
      // First, fetch user profile information from Google
      const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: {
          Authorization: `Bearer ${tokenResponse.access_token}`,
        },
      });
      if (!userInfoResponse) {
        setError("Google sign-up failed. Please try again or use another method.");
        setIsLoadingGoogle(false);
        setIsLoading(false);
        return
      }

      let jobID = "";
      if (queryParams.get("ref") && getCookie('__apply_job_guest__')) {
        jobID = queryParams.get("ref") || "";
      }

      const userInfo = await userInfoResponse.json();
      if (userInfo && userInfo.email === "") {
        setError("We couldn’t access your email from Google. Please allow permission to continue.");
        setIsLoadingGoogle(false);
        setIsLoading(false);
        return
      }

      // Send the access token to our backend for verification. When this
      // is a first-time Google signup the BE creates the user, so attach
      // any temp supporting-file uuids here too (demo-only, omitted when
      // empty so the legacy payload stays unchanged).
      const supportingUuidsGoogle = readSupportingFileUuids();
      const loginResponse = await authJobSeekerLoginWithGoogle({
        email: userInfo.email,
        first_name: userInfo.given_name,
        last_name: userInfo.family_name,
        sub: userInfo.sub,
        job_id: jobID,
        referral_code: sessionStorage.getItem("__referral_reg_code__") || null,
        ...(supportingUuidsGoogle.length > 0
          ? { supporting_file_uuids: supportingUuidsGoogle }
          : {}),
      });

      if (loginResponse?.status === 200) {
        setUserGoogleInfo(userInfo);
        setCurrentUserID(loginResponse.data?.user?.id || "");
        setCurrentPage("confirm-details");
        setCurrentStep(2);
        setIsLoadingGoogle(false);
        setIsLoading(false);
        // Set the auth cookie eagerly on Google login success — this
        // way, if /cv/process-uploaded-cv fails and we drop into the
        // re-upload retry flow, the retry can call processUploadedCV
        // directly without re-hitting /auth/job-seeker/google.
        setCookie(
          "authToken",
          loginResponse?.data?.token?.access_token || "",
          import.meta.env.VITE_AUTH_TOKEN_EXPIRED_DAY
        );
        setProcessUploadCV(true);
        const dataForm = getCookie('__apply_job_guest__');
        let respProcessUploadCV: unknown;
        // Local flag — `isHasErrorUpload` from state is stale within
        // this same async tick, so reading it after the catch wouldn't
        // reflect the just-applied setHasErrorUpload(true) and would
        // wipe the BE error message we just stored. Track the catch
        // locally instead.
        let processCVThrew = false;
        try {
          respProcessUploadCV = await processUploadedCV(
            sessionStorage.getItem("cv_uuid") || "",
            loginResponse.data?.user?.id || "",
            dataForm?.name || null,
            dataForm?.country_code || null,
            dataForm?.phone || null,
            dataForm?.email || null,
          )
        } catch (err) {
          processCVThrew = true;
          const message = extractProcessCVError(err);
          setProcessCVError(null);
          setProcessCVMessage(message);
          setHasErrorUpload(true);
          respProcessUploadCV = undefined;
        }
        if (respProcessUploadCV) {
          setProcessCVError(null);
          setProcessCVMessage(null);
        } else if (!processCVThrew) {
          // Soft failure (falsy response without an exception). Don't
          // touch processCVMessage here — keep whatever the catch put
          // there, or leave it null.
          setHasErrorUpload(true);
        }

        setProcessUploadCV(false);
      } else {
        setError(loginResponse.message || "");
        setIsLoadingGoogle(false);
        setIsLoading(false);
        setProcessUploadCV(false);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response: { status: number; data: { data: { company_name: string; expected_subdomain: string } } } };
        if (axiosError.response.status === 403) {
          setIsOpenDialogBYOC(true)
        }
      }
      setError("Something went wrong. Please try again.");
      console.error("Error during Google signup:", error);
      setIsLoadingGoogle(false);
      setProcessUploadCV(false);
    }
  };

  const callObject = useCallObject({}) as DailyCall;

  const handleInterviewComplete = () => {
    // End the interview if it's still active
    if (videoInterviewRef.current) {
      videoInterviewRef.current.endInterview('interview_completed');
    }

    setLoadingSendWelcomeEmail(true);
    authSendWelcomEmail({
      finished_interview: true,
      job_id: queryParams.get("ref") || "",
      eventTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      eventTimestampLocal: new Date().toISOString(),
      deviceInfo: `Audio ${localStorage.getItem('preferredAudioInputId') || ''}, Video ${localStorage.getItem('preferredVideoId') || ''}, Speaker ${localStorage.getItem('preferredAudioOutputId') || ''}`,
    }).catch((error) => {
      console.error("Error sending welcome email:", error);
    }).finally(() => {
      setCurrentPage("ai-interview-completed");
      setCurrentStep(4)
      setLoadingSendWelcomeEmail(false);
    });
  };

  const handleSkipInterivew = () => {
    setLoadingSendWelcomeEmail(true);
    authSendWelcomEmail({
      finished_interview: false,
      job_id: queryParams.get("ref") || "",
      eventTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      eventTimestampLocal: new Date().toISOString(),
      deviceInfo: `Audio ${localStorage.getItem('preferredAudioInputId') || ''}, Video ${localStorage.getItem('preferredVideoId') || ''}, Speaker ${localStorage.getItem('preferredAudioOutputId') || ''}`,
    }).then((resp) => {
      if (resp) {
        setCurrentPage("ai-interview-completed")
        setSkipInterview(true)
        setCurrentStep(4)
      }
    }).catch((error) => {
      catchErrorLog(`failed to send welcome email, err: ${error}`)
    }).finally(() => {
      setLoadingSendWelcomeEmail(false);
    });
  }

  let newCurrentStep = currentStep;
  if (queryParams.get("ref") && currentStep === 3) {
    newCurrentStep = currentStep - 1;
  }

  return (
    <>
      <BasicLayout
        disableMinHeight
        showFooter={false}
        style={{
          height: "calc(100vh - 97px)",
        }}
      >
        <div className="flex flex-col justify-center w-full">
          {currentPage === "resume-upload" && (
            <div className="flex flex-col items-center justify-center w-full min-h-[510px] px-4 py-8">
              <div className="mx-auto mt-2 md:mt-6 flex flex-col items-center">
                <div className="text-left mb-8 md:mb-12 w-full">
                  {/* SPA is an assessment partner — their candidates land here
                      from their own subdomain, so their mark leads the page,
                      above the headline that names them. Rendered only when
                      preferences actually carry a logo: an empty value would
                      build "<asset-base>/" and show a broken image. onError
                      covers the path being set but the asset missing — the
                      block disappears rather than leaving a broken icon over
                      the title. */}
                  {isSpaSubdomain() && companyInfo?.preferences?.company_logo && (
                    <div className="flex justify-center mb-4 md:mb-6">
                      <img
                        src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${companyInfo.preferences.company_logo}`}
                        alt={companyInfo?.company_name || "Company logo"}
                        className="h-14 md:h-20 w-auto object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  )}
                  <h1 className="text-2xl md:text-5xl font-bold text-gray-900 dark:text-white mb-2 md:mb-4 text-center">
                    {!companyInfo?.is_byoc ? (
                      isDemoPracticeMode() ? (
                        <>Prepare for your practice interview</>
                      ) : (
                        <>Apply to get hired by the best.</>
                      )
                    ) : (
                      <>
                        Be part of what’s next at {companyInfo?.company_name}.
                      </>
                    )}
                  </h1>
                  <p className="text-sm md:text-xl text-gray-600 dark:text-gray-300 text-center">
                    {!companyInfo?.is_byoc ? (
                      isDemoPracticeMode() ? (
                        <>Practice with AI-powered interview simulations designed for the public service.</>
                      ) : (
                        <>
                          <span className="text-blue-700">ourteam</span> gets you considered by hundreds of companies with one single application.
                        </>
                      )
                    ) : (
                      <>
                        Every great career with <span className="text-blue-700">{companyInfo?.company_name}</span> starts right here.
                      </>
                    )}

                  </p>
                </div>
              </div>
              <div className="flex flex-col items-center justify-center md:min-w-[800px]">
                {import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true" ? (
                  <ResumeAndFilesUpload
                    onUploadComplete={() => {
                      setCurrentPage("login");
                      setCurrentStep(1);
                      setIsLoading(false);
                    }}
                  />
                ) : (
                  <ResumeUpload
                    onUploadComplete={() => {
                      setCurrentPage("login");
                      setCurrentStep(1);
                      setIsLoading(false);
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {currentPage === "login" && (
            <div className="flex flex-col items-center justify-center w-full min-h-[510px] px-4 py-8 mt-6 md:mt-4">
              <JobseekerFirstTimeRegister
                error={error}
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                confirmPassword={confirmPassword}
                setConfirmPassword={setConfirmPassword}
                onRegister={handleLoginComplete}
                onGoogleSignup={handleGoogleSignup}
                isAuthGoogleLoading={isLoadingGoogle || isLoading}
              />
            </div>
          )}

          {currentPage === "verify-email" && (
            <div className="flex flex-col items-center justify-center w-full min-h-[510px] px-4">
              <CandidateEmailOTPVerify
                email={email}
                onNextStep={() => {
                  setProcessUploadCV(true)
                  setCurrentPage("confirm-details")
                  setCurrentStep(2)
                  handleSuccessVerifyEmail()
                }}
              />
            </div>
          )}


          {(currentPage === "confirm-details") && (
            <div className="flex flex-col items-center justify-center w-full min-h-[510px] px-4 py-8 mt-[4.5%] sm:mb-[96px]">
              {!isHasErrorUpload ? (
                <div className="flex flex-col items-center justify-center min-h-[240px] w-full md:w-[800px] mb-10 sm:mb-0">
                  <JobseekerConfirmDetails
                    onSave={handleProfileSave}
                    isLoading={isProcessUploadCV || isLoading}
                  />
                </div>
              ) : (
                  <div className="flex flex-col items-center justify-center min-h-[240px] w-full md:w-[800px] space-y-6">
                    <div className="text-center">
                      <h2 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                        We couldn't process your resume.
                      </h2>
                      {processCVError && (
                        <p className="text-sm md:text-base text-red-600 dark:text-red-400 mt-2">
                          {processCVError}
                        </p>
                    )}
                      <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 mt-2">
                        {processCVMessage
                          || "Please re-upload your CV file below. We'll try parsing it again automatically."}
                      </p>
                    </div>
                    {/* Re-upload widget: writes the new cv_uuid into
                      sessionStorage on success, then onUploadComplete
                      kicks off handleRetryProcessUploadCV which reads
                      that fresh uuid and re-calls
                      /cv/process-uploaded-cv. */}
                    {import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true" ? (
                      <ResumeAndFilesUpload
                        onUploadComplete={() => {
                          setHasErrorUpload(false);
                          handleRetryProcessUploadCV();
                        }}
                      />
                    ) : (
                      <ResumeUpload
                        onUploadComplete={() => {
                          setHasErrorUpload(false);
                          handleRetryProcessUploadCV();
                        }}
                      />
                    )}
                    {isProcessUploadCV && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                        Re-processing your resume...
                      </div>
                    )}
                </div>
              )}
            </div>
          )}

          {currentPage === "ai-interview" && (
            <div className="container max-w-6xl mx-auto px-0 sm:px-4 sm:py-8 flex flex-col min-h-[510px] items-center justify-center">
              {isLoadingAIInterview ? (
                <div>
                  <p className="text-lg text-gray-700">Loading AI Interview...</p>
                </div>
              ) : interviewAssessment ? (
                <DailyProvider callObject={callObject}>
                  <SpeechDetectionProvider
                    volumeThreshold={30}
                    silenceDelay={400}
                    autoStart={true}
                  >
                    <CustomVoiceInterviewV2
                      ref={videoInterviewRef}
                      interviewId={interviewAssessment.id as string}
                      pipecatApiUrl={import.meta.env.VITE_PIPECAT_API_URL || ""}
                      agentName={import.meta.env.VITE_PIPECAT_CUSTOM_AGENT_NAME || ""}
                      authToken={import.meta.env.VITE_PIPECAT_AUTH_TOKEN || ""}
                      s3BucketName={import.meta.env.VITE_S3_BUCKET_NAME || ""}
                      s3BucketRegion={import.meta.env.VITE_S3_BUCKET_REGION || ""}
                      awsAssumeRoleArn={import.meta.env.VITE_AWS_ASSUME_ROLE_ARN || ""}
                      fastapiURL={import.meta.env.VITE_TALENT_AI_BE_BASE_URL || ""}
                      userId={currentUserID}
                      userCV={formatCV(cvData)}
                      userName={cvData?.name || ''}
                      isSkipInterview={false}
                        isLoadingSkipInterview={isLoadingSendWelcomeEmail}
                        onSkipInterview={handleSkipInterivew}
                        showSkipLaterButton={true}
                      sections={formattedSections}
                      interviewName={interviewAssessment.content.interviewName}
                      description={interviewAssessment.content.description}
                      maxDuration={interviewAssessment.content.maxDuration || 20}
                      interviewContext={interviewAssessment.content.interviewContext || "You are an interviewer for a job position."}
                      closingStatement={interviewAssessment.content.closingStatement || ""}
                      interviewerPersona={interviewAssessment.content.interviewerPersona || ""}
                      toneAndCommunication={interviewAssessment.content.toneAndCommunication || undefined}
                      interviewFlowPrinciples={interviewAssessment.content.interviewFlowPrinciples || undefined}
                      informationToShare={interviewAssessment.content.informationToShare || undefined}
                      customGreeting={interviewAssessment.content.customGreeting || ""}
                      job_id={jobDetail?.id}
                      company_id={jobDetail?.company_id}
                      interviewLanguage={interviewAssessment.content.interviewLanguage || "en-US"}
                      voice={interviewAssessment.content.voice || "male"}
                      idleTimeoutMessage={interviewAssessment.content.idleTimeoutMessage}
                      hasFormAssessment={false}
                      hasEligibilityCheck={false}
                      onCloseModal={handleInterviewComplete}
                      onCompletedInterview={() => {
                        console.log("Jobseeker signup AI interview completed");
                      }}
                    />
                  </SpeechDetectionProvider>
                </DailyProvider>
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <p className="text-lg text-gray-700 text-center">
                    AI interview is not available right now. Please refresh the page and try again.
                  </p>
                  <Button
                    variant="outline"
                    onClick={loadInterviewAssessment}
                    disabled={isLoadingAIInterview}
                    className="gap-2"
                  >
                    <RefreshCwIcon className={`w-4 h-4 ${isLoadingAIInterview ? "animate-spin" : ""}`} />
                    {isLoadingAIInterview ? "Loading..." : "Try again"}
                  </Button>
                </div>
              )}
            </div>
          )}

          {
            currentPage === "ai-interview-completed" && (
              <div className="flex flex-col items-center justify-center w-full px-4 py-8 min-h-[510px] mt-[4.5%]">
                <CandidateAIInterviewCompleted
                  isSkipInterview={isSkipInterview}
                  redirect_path={userRedirectPath}
                />
              </div>
            )
          }

          {
            currentPage === "thank-you" && (
              <div className="flex flex-col items-center justify-center w-full px-4 py-8 min-h-[510px] mt-[4.5%]">
                <ThankYou
                  title={queryParams.get("ref") ? "Registration Completed!" : "Thank you!"}
                  message={queryParams.get("ref") ? (
                    <span>Your account has been created and your profile is set up.<br />Continue to apply for the job and complete your application process.</span>
                  ) : "Your profile has been created successfully."}
                  isDomainExist={false}
                  redirect_path={userRedirectPath}
                  role="job_seeker"
                />
              </div>
            )
          }
        </div>
      </BasicLayout >


      {currentPage !== "login" && (
        <div className="w-full fixed bottom-0 left-0 right-0 px-4 sm:px-8 pt-4 pb-4 sm:pb-4 bg-white shadow-sm">
          <ProgressBar
            currentStep={newCurrentStep + 1}
            steps={progressSteps}
            companyPreferences={companyInfo?.preferences}
            isShowBanding={!!companyInfo?.preferences}
          />
        </div>
      )}

      <BYOCDomainRegisterPopup
        isOpen={isOpenDialogBYOC}
        onClose={() => setIsOpenDialogBYOC(false)}
        userEmail={email}
      />
    </>
  );
};

export default JobseekerSignup;